<script setup lang="ts">
import { computed, reactive } from "vue";

import { ButtonSeverity, TuButton, TuInputSwitch, TuInputText } from "@tinyurldev/tu-ui-vue";

import { logError } from "@Resources/js/helpers/logger";
import XOverlay from "@Modules/shared/store/XOverlay";
import { useTranslation } from "@Resources/js/composables";
import type { Domain } from "@Modules/urls/Data/Models/DomainModel";
import PopoverOverhauling from "@Components/popover/PopoverOverhauling.vue";
import type { BrandedDomainCheckResult } from "@Modules/teams/Api/DomainsApi";
import DomainInfoHeader from "@Modules/shared/Components/DomainInfoHeader.vue";

import CheckDomain from "./CheckDomain.vue";
import CheckSubDomain from "./CheckSubDomain.vue";
import DnsRecordsTable from "./DnsRecordsTable.vue";

//
// Setup
const overlay = XOverlay();
const { __ } = useTranslation();

//
// Props
interface Props {
  domain: Domain;
  nameservers: string[];
  personalDomain?: string;
  isRenewalEnabled: boolean;
  checkDomain: (domain: string) => Promise<BrandedDomainCheckResult>;
  checkSubDomain: (domain: string) => Promise<BrandedDomainCheckResult>;
  toggleDisableDomain: (domain: string) => Promise<boolean>;
  setDomainRootRedirect: (domain: string, enabled: boolean, destination: string) => Promise<boolean>;
  setDomainErrorRedirect: (domain: string, enabled: boolean, destination: string) => Promise<boolean>;
  setDomainRenewal: (domain: string, enabled: boolean) => Promise<boolean>;
}

const props = defineProps<Props>();

//
// Emit
interface Events {
  (e: "delete", domain: Domain): void;
  (e: "update"): void;
}

const emit = defineEmits<Events>();

//
// Data
type RedirectState = {
  isBusy: boolean;
  enabled: boolean;
  destination: string;
  destinationError?: string | null;
};

const allowUrlsState = reactive({
  isBusy: false,
  isEnabled: !props.domain.isDisabled,
});

const rootRedirectState = reactive<RedirectState>({
  isBusy: false,
  enabled: props.domain.isDomainRootRedirectEnabled,
  destination: props.domain.rootRedirectUrl ?? "",
  destinationError: null,
});

const errorRedirectState = reactive<RedirectState>({
  isBusy: false,
  enabled: props.domain.isDomainErrorRedirectEnabled,
  destination: props.domain.errorRedirectUrl ?? "",
  destinationError: null,
});

const renewalState = reactive({
  isBusy: false,
  enabled: props.isRenewalEnabled,
});

//
// Computed
const isRootRedirectSaveable = computed(() =>
  props.domain.rootRedirectUrl !== rootRedirectState.destination
  || props.domain.isDomainRootRedirectEnabled !== rootRedirectState.enabled,
);

const isErrorRedirectSaveable = computed(() =>
  props.domain.errorRedirectUrl !== errorRedirectState.destination
  || props.domain.isDomainErrorRedirectEnabled !== errorRedirectState.enabled,
);

//
// Methods
function onToggleAllow(isAllow: boolean): void {
  allowUrlsState.isBusy = true;
  allowUrlsState.isEnabled = isAllow;

  props.toggleDisableDomain(props.domain.domain)
    .then((result) => {
      if (!result) {
        allowUrlsState.isEnabled = !isAllow;

        return;
      }

      emit("update");
    })
    .catch(() => {
      logError(`[EditDomainPopover][onToggleAllow]: Toggle disable domain could not be set for domain ${props.domain.domain}.`);
    })
    .finally(() => { allowUrlsState.isBusy = false; });
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

function saveRootRedirect(): void {
  if (!isRootRedirectSaveable.value) {
    return;
  }

  if (!isValidUrl(rootRedirectState.destination) || rootRedirectState.destination === "") {
    rootRedirectState.destinationError = __("organization_domains_settings.edit_domain_popover.invalid_destination_url");
    return;
  }

  rootRedirectState.isBusy = true;

  props.setDomainRootRedirect(
    props.domain.domain,
    rootRedirectState.enabled,
    rootRedirectState.destination,
  )
  .then((result) => {
    if (!result) {
      rootRedirectState.enabled = !rootRedirectState.enabled;
      rootRedirectState.destination = "";

      return;
    }

    emit("update");
  })
  .catch(() => {
    logError(`[EditDomainPopover][saveRootRedirect]: Save root redirect could not be set for domain ${props.domain.domain}.`);
  })
  .finally(() => rootRedirectState.isBusy = false);
}

function saveErrorRedirect(): void {
  if (!isErrorRedirectSaveable.value) {
    return;
  }

  if (!isValidUrl(errorRedirectState.destination) || errorRedirectState.destination === "") {
    errorRedirectState.destinationError = __("organization_domains_settings.edit_domain_popover.invalid_destination_url");
    return;
  }

  errorRedirectState.isBusy = true;

  props.setDomainErrorRedirect(
    props.domain.domain,
    errorRedirectState.enabled,
    errorRedirectState.destination,
  ).then((result) => {
    if (!result) {
      errorRedirectState.enabled = !errorRedirectState.enabled;
      errorRedirectState.destination = "";

      return;
    }

    emit("update");
  })
  .catch(() => {
    logError(`[EditDomainPopover][saveErrorRedirect]: Save error redirect could not be set for domain ${props.domain.domain}.`);
  })
  .finally(() => errorRedirectState.isBusy = false);
}

function saveRenewal(enabled: boolean): void {
  renewalState.isBusy = true;
  renewalState.enabled = enabled;

  props.setDomainRenewal(props.domain.domain, enabled)
    .then((result) => {
      if (!result) {
        renewalState.enabled = !enabled;

        return;
      }

      emit("update");
    })
    .catch(() => {
      logError(`[EditDomainPopover][saveRenewal]: Save renewal could not be set for domain ${props.domain.domain}.`);
    })
    .finally(() => { renewalState.isBusy = false; });
}

function onToggleRoot(enabled: boolean): void {
  rootRedirectState.enabled = enabled;

  if (!enabled) {
    saveRootRedirect();
  }
}

function onToggleError(enabled: boolean): void {
  errorRedirectState.enabled = enabled;

  if (!enabled) {
    saveErrorRedirect();
  }
}

//
// Exposed Test Vars
defineExpose({ allowUrlsState, rootRedirectState, errorRedirectState, renewalState, isRootRedirectSaveable, isErrorRedirectSaveable });
</script>

<template>
  <PopoverOverhauling @close="overlay.hide()">
    <template #title>
      {{ __("organization_domains_settings.edit_domain_popover.title") }}
    </template>

    <div class="edit-domain-popover">
      <DomainInfoHeader :domain="domain" />

      <div class="edit-domain-popover__toggle-wrapper">
        <div class="edit-domain-popover__toggle">
          <p class="edit-domain-popover__toggle-title">
            {{ __("organization_domains_settings.edit_domain_popover.allow_urls_label") }}
          </p>
          <TuInputSwitch
            class="_allow-urls-toggle"
            :value="allowUrlsState.isEnabled"
            :heading="allowUrlsState.isEnabled ? __('organization_domains_settings.edit_domain_popover.enabled_heading') : __('organization_domains_settings.edit_domain_popover.disabled_heading')"
            :is-disabled="allowUrlsState.isBusy"
            @input="onToggleAllow"
          />
        </div>

        <template v-if="!domain.isVanitySubdomain">
          <div class="edit-domain-popover__toggle">
            <p class="edit-domain-popover__toggle-title">
              {{ __("organization_domains_settings.edit_domain_popover.root_redirect_label") }}
            </p>
            <TuInputSwitch
              class="_root-redirect-toggle"
              :value="rootRedirectState.enabled"
              :heading="rootRedirectState.enabled ? __('organization_domains_settings.edit_domain_popover.enabled_heading') : __('organization_domains_settings.edit_domain_popover.disabled_heading')"
              :is-disabled="rootRedirectState.isBusy"
              @input="onToggleRoot"
            />
          </div>
          <div
            v-if="rootRedirectState.enabled"
            class="edit-domain-popover__root-redirect"
          >
            <TuInputText
              class="edit-domain-popover__root-redirect-input _root-redirect-destination"
              :value="rootRedirectState.destination"
              :input="{ placeholder: __('organization_domains_settings.edit_domain_popover.destination_placeholder') }"
              :copy="{ ...(rootRedirectState.destinationError && { errorMessage: rootRedirectState.destinationError }) }"
              @input="(value) => rootRedirectState.destination = value"
              @focus="rootRedirectState.destinationError = ''"
            />
            <TuButton
              class="_root-redirect-save"
              :label="__('organization_domains_settings.edit_domain_popover.save_button')"
              :is-disabled="rootRedirectState.isBusy || !isRootRedirectSaveable"
              :is-loading="rootRedirectState.isBusy"
              @click.stop="saveRootRedirect"
            />
          </div>

          <div class="edit-domain-popover__toggle">
            <p class="edit-domain-popover__toggle-title">
              {{ __("organization_domains_settings.edit_domain_popover.error_redirect_label") }}
            </p>
            <TuInputSwitch
              class="_error-redirect-toggle"
              :value="errorRedirectState.enabled"
              :heading="errorRedirectState.enabled ? __('organization_domains_settings.edit_domain_popover.enabled_heading') : __('organization_domains_settings.edit_domain_popover.disabled_heading')"
              :is-disabled="errorRedirectState.isBusy"
              @input="onToggleError"
            />
          </div>
          <div
            v-if="errorRedirectState.enabled"
            class="edit-domain-popover__error-redirect"
          >
            <TuInputText
              class="edit-domain-popover__error-redirect-input _error-redirect-destination"
              :value="errorRedirectState.destination"
              :input="{ placeholder: __('organization_domains_settings.edit_domain_popover.destination_placeholder') }"
              :copy="{ ...(errorRedirectState.destinationError && { errorMessage: errorRedirectState.destinationError }) }"
              @input="(value) => errorRedirectState.destination = value"
              @focus="errorRedirectState.destinationError = ''"
            />
            <TuButton
              class="_error-redirect-save"
              :label="__('organization_domains_settings.edit_domain_popover.save_button')"
              :is-disabled="errorRedirectState.isBusy || !isErrorRedirectSaveable"
              :is-loading="errorRedirectState.isBusy"
              @click.stop="saveErrorRedirect"
            />
          </div>
        </template>

        <div
          v-if="domain.isPurchased"
          class="edit-domain-popover__toggle"
        >
          <p class="edit-domain-popover__toggle-title">
            {{ __("organization_domains_settings.edit_domain_popover.renewal_label") }}
          </p>
          <TuInputSwitch
            class="_renewal-toggle"
            :value="renewalState.enabled"
            :heading="renewalState.enabled ? __('organization_domains_settings.edit_domain_popover.auto_heading') : __('organization_domains_settings.edit_domain_popover.manual_heading')"
            :is-disabled="renewalState.isBusy"
            @input="saveRenewal"
          />
        </div>

        <div
          v-if="domain.isActive"
          class="edit-domain-popover__alert edit-domain-popover__alert--success"
        >
          {{ __("organization_domains_settings.edit_domain_popover.active_message") }}
        </div>
        <div
          v-else-if="!domain.isPurchased"
          class="edit-domain-popover__alert edit-domain-popover__alert--warning"
        >
          {{ __("organization_domains_settings.edit_domain_popover.not_active_message") }}
        </div>
      </div>

      <CheckDomain
        v-if="domain.isApex"
        :domain="domain.domain"
        :nameservers="nameservers"
        :check-domain="checkDomain"
      />

      <CheckSubDomain
        v-else-if="!domain.isVanitySubdomain"
        :domain="domain.domain"
        :personal-domain="personalDomain"
        :check-sub-domain="checkSubDomain"
      />

      <DnsRecordsTable :records="domain.dns" />

      <div class="edit-domain-popover__ssl-notice">
        <h5 class="edit-domain-popover__ssl-notice-title">
          <i class="fa fa-lock text-success" />
          {{ __('organization_domains_settings.edit_domain_popover.ssl_notice_title') }}
        </h5>
        <p class="edit-domain-popover__ssl-notice-content">
          {{ __('organization_domains_settings.edit_domain_popover.ssl_notice_content') }}
        </p>
      </div>

      <TuButton
        class="_delete"
        :label="__('organization_domains_settings.edit_domain_popover.delete_button')"
        :severity="ButtonSeverity.ALERT"
        @click="emit('delete', domain)"
      />
    </div>
  </PopoverOverhauling>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;

.edit-domain-popover {
  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;

  &__toggle-wrapper {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxxs;
  }

  &__toggle {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
  }

  &__toggle-title {
    font-size: typography.$s-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$s-line-height;
  }

  &__root-redirect {
    display: flex;
    align-items: flex-start;
    gap: spacing.$xxxs;
  }

  &__root-redirect-input {
    flex: 1;
  }

  &__error-redirect {
    display: flex;
    align-items: flex-start;
    gap: spacing.$xxxs;
  }

  &__error-redirect-input {
    flex: 1;
  }

  &__alert {
    padding: spacing.$xxs;
    border-radius: border.$medium-radius;

    &--success {
      background-color: color.$secondary-light;
      color: color.$secondary-dark;
    }

    &--warning {
      background-color: color.$alert-light;
      color: color.$alert-dark;
    }
  }

  &__toggle-title {
    margin: 0;
  }
}
</style>
